// src/pages/LoginPage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance'; // ✅ use axiosInstance
import bgImage from '../assets/imss.png'; // ✅ local background
import {
  Eye,
  EyeOff,
  Lock,
  Warehouse,
  ClipboardList,
  Truck,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1 = login, 2 = verify otp, 3 = forgot password, 4 = reset password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [showResendButton, setShowResendButton] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // ✅ Send OTP
  const handleSendOtp = async (e) => {
    e && e.preventDefault();
    setError('');
    if (!captchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post('auth/login', { email, password, captchaToken });
      alert(res.data.message || 'OTP sent successfully');
      setStep(2);
      setTimer(60);
      setShowResendButton(false);
      setCaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
      setCaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axiosInstance.post('auth/verify-otp', { email, otp });
      const data = res.data;

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.data));

      const role = data?.data?.roles?.[0]?.name;
      if (role === 'Admin') navigate('/');
      else if (role === 'Branch') navigate('/');
      else navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Forgot Password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axiosInstance.post('user/profile/forgot-password', {
        email: forgotPasswordEmail,
      });
      alert(res.data.message || 'OTP sent successfully');
      setStep(4);
      setTimer(60);
      setShowResendButton(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset OTP');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post('user/profile/reset-password', {
        email: forgotPasswordEmail,
        otp: resetOtp,
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      });
      alert(res.data.message || 'Password reset successful. Please login.');
      setStep(1);
      setNewPassword('');
      setConfirmPassword('');
      setResetOtp('');
    } catch (err) {
      // API returns { errors: [{field, error}] } on validation failure
      const apiErrors = err.response?.data?.errors;
      if (apiErrors && Array.isArray(apiErrors) && apiErrors.length > 0) {
        setError(apiErrors.map((e) => e.error).join(' | '));
      } else {
        setError(err.response?.data?.message || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Timer effect for resend button
  useEffect(() => {
    let interval;
    if ((step === 2 || step === 4) && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setShowResendButton(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  return (
    <div className="w-full min-h-[600px] md:h-[calc(100vh-260px)] flex flex-col md:flex-row overflow-hidden bg-white">
      {/* Left Panel: Glowing constellation + stats + image bg */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="hidden md:flex md:w-1/2 lg:w-[55%] xl:w-[60%] h-full relative overflow-hidden flex-col justify-between p-8 lg:p-12 text-white bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        {/* Transparent blue overlay */}
        <div className="absolute inset-0 bg-[#003366] bg-opacity-75 z-0"></div>

        {/* Constellation SVG Network (India representation) */}
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <svg
            className="w-[85%] h-[85%] opacity-30 absolute right-4 bottom-4 max-w-[500px]"
            viewBox="0 0 400 450"
          >
            <defs>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            {/* Connection lines */}
            <line
              x1="170"
              y1="100"
              x2="90"
              y2="240"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
              strokeDasharray="4,4"
            />
            <line
              x1="170"
              y1="100"
              x2="280"
              y2="200"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
              strokeDasharray="4,4"
            />
            <line
              x1="170"
              y1="100"
              x2="160"
              y2="190"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="90"
              y1="240"
              x2="160"
              y2="190"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="90"
              y1="240"
              x2="130"
              y2="320"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="130"
              y1="320"
              x2="160"
              y2="340"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="160"
              y1="340"
              x2="160"
              y2="270"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="160"
              y1="270"
              x2="130"
              y2="320"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="160"
              y1="270"
              x2="280"
              y2="200"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
            />
            <line
              x1="280"
              y1="200"
              x2="340"
              y2="160"
              stroke="rgba(96, 165, 250, 0.4)"
              strokeWidth="1.5"
              strokeDasharray="4,4"
            />
            {/* Nodes */}
            <circle
              cx="170"
              cy="100"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Delhi */}
            <circle
              cx="90"
              cy="240"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Mumbai */}
            <circle
              cx="130"
              cy="320"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Bengaluru */}
            <circle
              cx="160"
              cy="340"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Chennai */}
            <circle
              cx="280"
              cy="200"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Kolkata */}
            <circle cx="160" cy="190" r="4" fill="#93c5fd" /> {/* Bhopal */}
            <circle cx="160" cy="270" r="4" fill="#93c5fd" /> {/* Hyderabad */}
            <circle
              cx="340"
              cy="160"
              r="5"
              fill="#60a5fa"
              filter="url(#glow)"
            />{' '}
            {/* Guwahati */}
          </svg>
        </div>

        {/* Floating Widgets */}
        {/* Available Stock Widget - Top Right */}

        {/* Blocked Stock Widget - Bottom Left */}

        {/* Warehouse Status Widget - Bottom Right */}

        {/* Content Top: Logistical Hex/Square Outline Icons */}
        <div className="relative z-20 w-full flex justify-center mt-2">
          <div className="flex gap-3">
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-300 shadow-sm">
              <Warehouse size={18} />
            </div>
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-300 shadow-sm">
              <ClipboardList size={18} />
            </div>
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-300 shadow-sm">
              <Truck size={18} />
            </div>
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-300 shadow-sm">
              <ShieldCheck size={18} />
            </div>
            <div className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-300 shadow-sm">
              <TrendingUp size={18} />
            </div>
          </div>
        </div>

        {/* Content Center: Branding and Text (Vertically and Horizontally Centered) */}
        <div className="flex-1 flex flex-col justify-center items-center text-center relative z-20 max-w-2xl mx-auto my-auto px-4">
          <h1 className="text-3xl lg:text-3xl xl:text-4xl font-extrabold tracking-tight leading-tight text-white mb-6 whitespace-nowrap">
            eStock Management <span className="text-blue-300">System</span>
          </h1>
          <p className="text-sm lg:text-base leading-relaxed text-slate-100 max-w-md">
            Access all your inventory services in one place. Manage, track, and
            explore with ease — delivering seamless functionality across the
            nation.
          </p>
        </div>

        {/* Dummy spacer bottom */}
        <div className="h-10 relative z-20"></div>
      </motion.div>

      {/* Right Panel: Clean form layout */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="w-full md:w-1/2 lg:w-[45%] xl:w-[40%] bg-white flex flex-col p-8 md:p-12 lg:p-16 h-full"
      >
        {/* Form Container (centered horizontally and vertically) */}
        <div className="flex-1 flex flex-col justify-center w-full max-w-[420px] mx-auto">
          <h2 className="text-3xl font-bold text-slate-800 tracking-tight mb-2">
            {step === 1
              ? 'Welcome back'
              : step === 2
                ? 'Verify OTP'
                : step === 3
                  ? 'Forgot Password'
                  : 'Reset Password'}
          </h2>
          <p className="text-slate-500 text-sm mb-8">
            {step === 1
              ? 'Sign in to your NCCF account to continue'
              : step === 2
                ? 'Enter the OTP sent to your email to continue'
                : step === 3
                  ? 'Enter your email address to request a reset OTP'
                  : 'Enter details to reset your password'}
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 border border-red-100 p-3.5 rounded-xl mb-6 text-sm text-center font-medium">
              {error}
            </div>
          )}

          <form
            className="flex flex-col gap-5"
            onSubmit={
              step === 1
                ? handleSendOtp
                : step === 2
                  ? handleVerifyOtp
                  : step === 3
                    ? handleForgotPassword
                    : handleResetPassword
            }
          >
            {/* Step 1: Login */}
            {step === 1 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    placeholder="email@nccfindia.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                    required
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(3);
                        setForgotPasswordEmail('');
                      }}
                      className="text-blue-600 hover:text-blue-700 text-xs font-semibold hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-4 pr-11 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors animate-none"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Google reCAPTCHA v2 Widget */}
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    onErrored={() => setCaptchaToken(null)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !captchaToken}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md shadow-blue-600/10 hover:shadow-blue-600/20 disabled:opacity-50 text-sm mt-2"
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#10b981] hover:bg-[#059669] text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md shadow-emerald-600/10 hover:shadow-emerald-600/20 disabled:opacity-50 text-sm"
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>

                <div className="flex justify-between items-center mt-2">
                  {!showResendButton && timer > 0 && (
                    <p className="text-slate-500 text-xs font-medium">
                      Resend OTP in{' '}
                      <span className="font-bold text-slate-700">{timer}s</span>
                    </p>
                  )}
                  {showResendButton && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="text-blue-600 hover:text-blue-700 text-xs font-bold hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Step 3: Forgot Password */}
            {step === 3 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md shadow-blue-600/10 hover:shadow-blue-600/20 disabled:opacity-50 text-sm"
                >
                  {loading ? 'Sending...' : 'Send Reset OTP'}
                </button>

                <div className="flex justify-center mt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-slate-500 hover:text-slate-700 text-xs font-bold flex items-center gap-1"
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}

            {/* Step 4: Reset Password */}
            {step === 4 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-4 pr-11 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-4 pr-11 py-3 border border-slate-200 rounded-xl outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white placeholder-slate-400 text-slate-800 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#10b981] hover:bg-[#059669] text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md shadow-emerald-600/10 hover:shadow-emerald-600/20 disabled:opacity-50 text-sm"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>

                <div className="flex justify-between items-center mt-2">
                  {!showResendButton && timer > 0 && (
                    <p className="text-slate-500 text-xs font-medium">
                      Resend OTP in{' '}
                      <span className="font-bold text-slate-700">{timer}s</span>
                    </p>
                  )}
                  {showResendButton && (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-blue-600 hover:text-blue-700 text-xs font-bold hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </motion.div>
    </div>
  );
}
